import { Component, computed, effect, inject, output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CementoCantidadServiciosSelection } from 'src/app/models/cemento-checkout';
import { CementoCheckoutFlowService } from 'src/app/services/cemento-checkout-flow.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { calculateCementoCheckoutTotal } from 'src/app/shared/utils/cemento-cotizacion.mapper';
import {
  CementoPoliticaPallet,
  cementProductActiveDeliveryOption,
  cementProductPriceWithIva,
} from 'src/app/models/cement-product';

const SACOS_PER_PALLET = 72;

interface PalletOption {
  value: number;
  label: string;
  hours: number;
}

@Component({
  selector: 'app-cemento-cantidad-servicios-section',
  templateUrl: './cemento-cantidad-servicios-section.component.html',
  styleUrls: ['./cemento-cantidad-servicios-section.component.css'],
})
export class CementoCantidadServiciosSectionComponent {
  private readonly fb = inject(FormBuilder);
  private readonly flow = inject(CementoCheckoutFlowService);

  readonly validChange = output<boolean>();

  form!: FormGroup;

  private readonly checkoutState = toSignal(this.flow.checkoutState);

  readonly palletOptions = computed<PalletOption[]>(() => {
    const product = this.checkoutState()?.producto?.product;
    const activeOption = product ? cementProductActiveDeliveryOption(product) : null;
    const pallets = activeOption?.politica_pallets ?? [];
    return [...pallets]
      .sort((a, b) => a.pallet - b.pallet)
      .map((item) => this.toPalletOption(item));
  });

  constructor() {
    this.initForm();
    this.syncDefaultQuantity();
  }

  private initForm(): void {
    this.form = this.fb.group({
      quantity: [null as number | null, [Validators.required]],
    });

    this.form.statusChanges.subscribe(() => {
      this.emitCurrentValidity();
    });
    this.form.valueChanges.subscribe(() => {
      this.emitCurrentValidity();
    });
    this.emitCurrentValidity();
  }

  resetSelection(): void {
    this.form.reset({ quantity: null });
    this.validChange.emit(false);
  }

  getSelection(): CementoCantidadServiciosSelection | null {
    if (!this.form.valid) {
      return null;
    }
    const quantityValue = this.form.get('quantity')?.value;
    const quantity = Number(quantityValue);
    if (!Number.isFinite(quantity)) {
      return null;
    }
    const selectedOption = this.palletOptions().find((option) => option.value === quantity);
    if (!selectedOption) {
      return null;
    }
    const product = this.checkoutState()?.producto?.product;
    const activeOption = product ? cementProductActiveDeliveryOption(product) : null;
    const unitPrice = cementProductPriceWithIva(activeOption) || cementProductPriceWithIva(product);
    const sacks = quantity * SACOS_PER_PALLET;
    return {
      pallets: quantity,
      quantity: sacks,
      leadHours: selectedOption?.hours,
      unitPrice,
      totalPrice: calculateCementoCheckoutTotal(unitPrice, quantity),
    };
  }

  private syncDefaultQuantity(): void {
    effect(() => {
      const control = this.form.get('quantity');
      const options = this.palletOptions();
      const savedQuantity = this.checkoutState()?.cantidadServicios?.quantity ?? null;
      const savedPallets = this.checkoutState()?.cantidadServicios?.pallets ?? null;
      const currentValue = control?.value as number | null;

      if (!control) {
        return;
      }

      const selectionValue = savedPallets ?? savedQuantity;
      if (selectionValue != null && options.some((option) => option.value === selectionValue)) {
        if (currentValue !== selectionValue) {
          control.setValue(selectionValue, { emitEvent: false });
        }
        return;
      }

      if (currentValue !== null) {
        control.setValue(null, { emitEvent: false });
      }
      this.emitCurrentValidity();
    });
  }

  private emitCurrentValidity(): void {
    const quantityValue = this.form.get('quantity')?.value;
    const quantity = Number(quantityValue);
    const hasPolicy = Number.isFinite(quantity)
      && this.palletOptions().some((option) => option.value === quantity);
    this.validChange.emit(this.form.valid && hasPolicy);
  }

  private toPalletOption(item: CementoPoliticaPallet): PalletOption {
    const palletLabel = item.pallet === 1 ? '1 pallet' : `${item.pallet} pallets`;
    const totalSacos = item.pallet * SACOS_PER_PALLET;
    return {
      value: item.pallet,
      label: `${palletLabel} (${totalSacos} sacos)`,
      hours: item.horas,
    };
  }
}
